"use client"

import { useState, useEffect, useCallback } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Progress, Alert, Input, FormGroup, Label } from "reactstrap"
import { FaPrint, FaExclamationTriangle, FaCheckCircle, FaImage } from "react-icons/fa"
import { toast } from "react-toastify"
import { listPrinters, silentPrint } from "../../../utils/qzTrayService"
import {
  BADGE_WIDTH,
  BADGE_HEIGHT,
  SHEET_GRID_COLS,
  SHEET_GRID_ROWS,
  SHEET_BADGES_PER_PAGE,
  SHEET_DPI_SCALE,
  A4_PAGE_SIZE_IN,
  loadImageAsync,
  composeGridSheet,
} from "./badgeRenderer"

// ---------------------------------------------------------------------------
// Where the back-of-badge design comes from.
//
// For now this is a STATIC file in the public folder — drop the sponsor
// page image at:
//   public/assets/images/badge-back.jpg
// (same "public/assets/images/" folder the front badge's PSF logo already
// loads from — see `dynamicImage()` in bulkprint.tsx / professional-badge-
// designer.tsx).
//
// Later, once the back design is uploaded/managed from the database, swap
// this constant for a prop/API value — everything else in this file
// (composition, preview, printing) already works off of whatever image URL
// it's given, so that swap is a one-line change.
// ---------------------------------------------------------------------------
const dynamicImage = (imagePath: string): string => `/assets/images/${imagePath}`
const BACK_BADGE_IMAGE_PATH = dynamicImage("badge-back.jpeg")

interface Props {
  isOpen: boolean
  onToggle: () => void
}

const BackBadgePrintModal = ({ isOpen, onToggle }: Props) => {
  const [backImage, setBackImage] = useState<HTMLImageElement | null>(null)
  const [isLoadingImage, setIsLoadingImage] = useState(false)
  const [imageError, setImageError] = useState<string>("")

  const [previewImage, setPreviewImage] = useState<string>("")
  const [isComposing, setIsComposing] = useState(false)

  const [availablePrinters, setAvailablePrinters] = useState<string[]>([])
  const [selectedPrinter, setSelectedPrinter] = useState<string>("")
  const [printersError, setPrintersError] = useState<string>("")

  const [numberOfPages, setNumberOfPages] = useState<number>(1)
  const [isPrinting, setIsPrinting] = useState(false)
  const [printProgress, setPrintProgress] = useState({ current: 0, total: 0 })

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setBackImage(null)
      setIsLoadingImage(false)
      setImageError("")
      setPreviewImage("")
      setIsComposing(false)
      setAvailablePrinters([])
      setSelectedPrinter("")
      setPrintersError("")
      setNumberOfPages(1)
      setIsPrinting(false)
      setPrintProgress({ current: 0, total: 0 })
    }
  }, [isOpen])

  // Discover QZ Tray printers as soon as the modal opens.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    listPrinters()
      .then((printers) => {
        if (cancelled) return
        const names = printers.map((p) => p.name)
        setAvailablePrinters(names)
        setPrintersError("")
        setSelectedPrinter((current) => current || names[0] || "")
      })
      .catch((err) => {
        if (cancelled) return
        setPrintersError(err?.message || "QZ Tray is not reachable. Install/start QZ Tray to print.")
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  // Load the back design image as soon as the modal opens.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setIsLoadingImage(true)
    setImageError("")
    loadImageAsync(BACK_BADGE_IMAGE_PATH)
      .then((img) => {
        if (cancelled) return
        setBackImage(img)
      })
      .catch((err) => {
        if (cancelled) return
        setImageError(
          `Could not load the back-of-badge image from ${BACK_BADGE_IMAGE_PATH}. ` +
            `Make sure the file exists in the public folder (public${BACK_BADGE_IMAGE_PATH}).`,
        )
        console.error("[BackBadgePrint] Failed to load back image:", err)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingImage(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  // Compose the 2x2 preview sheet as soon as the image is ready. Since
  // every cell on a back sheet shows the same sponsor design, the same
  // image is simply repeated across all SHEET_BADGES_PER_PAGE cells.
  const composePreview = useCallback(async () => {
    if (!backImage) return
    setIsComposing(true)
    try {
      const sheet = composeGridSheet(
        Array(SHEET_BADGES_PER_PAGE).fill(backImage),
        BADGE_WIDTH,
        BADGE_HEIGHT,
      )
      setPreviewImage(sheet)
    } catch (err: any) {
      console.error("[BackBadgePrint] Failed to compose preview:", err)
      toast.error(`Failed to compose back-sheet preview: ${err?.message || "Unknown error"}`)
    } finally {
      setIsComposing(false)
    }
  }, [backImage])

  useEffect(() => {
    if (backImage) {
      composePreview()
    }
  }, [backImage, composePreview])

  const dataUrlToBase64 = (dataUrl: string): string => dataUrl.substring(dataUrl.indexOf(",") + 1)

  const handleSecurePrint = async () => {
    if (!previewImage) {
      toast.error("Nothing to print yet — the back-sheet preview hasn't been composed.")
      return
    }
    if (!selectedPrinter) {
      toast.error("Select a printer before printing.")
      return
    }
    if (!numberOfPages || numberOfPages < 1) {
      toast.error("Enter a valid number of sheets to print (1 or more).")
      return
    }

    setIsPrinting(true)
    setPrintProgress({ current: 0, total: numberOfPages })

    let successCount = 0
    const base64Data = dataUrlToBase64(previewImage)

    for (let p = 0; p < numberOfPages; p++) {
      try {
        // Every sheet is identical (same static back design in all four
        // quadrants), so the same composed image is sent once per copy —
        // this mirrors how the front bulk printer sends one QZ job per
        // physical A4 sheet.
        await silentPrint({
          printerName: selectedPrinter,
          base64Data,
          format: "image",
          size: A4_PAGE_SIZE_IN,
        })
        successCount++
      } catch (err: any) {
        console.error(`[BackBadgePrint] Failed to print sheet ${p + 1}:`, err)
        toast.error(`Failed to print sheet ${p + 1}: ${err?.message || "Unknown error"}`)
      }
      setPrintProgress({ current: p + 1, total: numberOfPages })
    }

    setIsPrinting(false)

    if (successCount === numberOfPages) {
      toast.success(
        `All ${numberOfPages} back sheet${numberOfPages > 1 ? "s" : ""} (${numberOfPages * SHEET_BADGES_PER_PAGE} badge backs) sent to ${selectedPrinter} successfully!`,
      )
    } else {
      toast.warning(`${successCount} of ${numberOfPages} sheets printed successfully.`)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={onToggle} size="lg" centered>
      <ModalHeader toggle={onToggle}>
        <FaImage className="me-2" />
        Print Badge Back (Sponsor Page)
      </ModalHeader>
      <ModalBody>
        <Alert color="info" className="small">
          The back of the badge is the same sponsor/emergency-contacts page for every badge, printed{" "}
          <strong>
            {SHEET_GRID_COLS}×{SHEET_GRID_ROWS} ({SHEET_BADGES_PER_PAGE} per sheet)
          </strong>{" "}
          on A4 — the same layout used for bulk front-badge printing, so a printed front sheet and a printed back
          sheet line up when flipped.
        </Alert>

        {isLoadingImage && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading back-of-badge image…</p>
          </div>
        )}

        {imageError && (
          <Alert color="danger">
            <FaExclamationTriangle className="me-2" />
            {imageError}
          </Alert>
        )}

        {printersError && (
          <Alert color="warning">
            <FaExclamationTriangle className="me-2" />
            {printersError}
          </Alert>
        )}

        {!isLoadingImage && !imageError && (
          <>
            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-6">
                <FormGroup className="mb-0">
                  <Label for="backPrinterSelect">Printer</Label>
                  <Input
                    id="backPrinterSelect"
                    type="select"
                    value={selectedPrinter}
                    onChange={(e) => setSelectedPrinter(e.target.value)}
                    disabled={availablePrinters.length === 0 || isPrinting}
                  >
                    {availablePrinters.length === 0 ? (
                      <option value="">No printers found</option>
                    ) : (
                      availablePrinters.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))
                    )}
                  </Input>
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup className="mb-0">
                  <Label for="numberOfPages">Number of Sheets to Print</Label>
                  <Input
                    id="numberOfPages"
                    type="number"
                    min={1}
                    value={numberOfPages}
                    onChange={(e) => setNumberOfPages(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
                    disabled={isPrinting}
                  />
                  <small className="text-muted">
                    {numberOfPages} sheet{numberOfPages !== 1 ? "s" : ""} ={" "}
                    {numberOfPages * SHEET_BADGES_PER_PAGE} badge backs
                  </small>
                </FormGroup>
              </div>
            </div>

            <div className="text-center mb-3">
              {isComposing ? (
                <div className="py-4">
                  <Spinner color="primary" size="sm" />
                  <span className="ms-2 text-muted">Composing preview…</span>
                </div>
              ) : previewImage ? (
                <div
                  className="bg-white border rounded shadow-sm mx-auto"
                  style={{
                    width: "100%",
                    maxWidth: "320px",
                    aspectRatio: `${A4_PAGE_SIZE_IN.width} / ${A4_PAGE_SIZE_IN.height}`,
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={previewImage || "/placeholder.svg"}
                    alt="Back sheet preview"
                    style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                  />
                </div>
              ) : null}
              <small className="text-muted d-block mt-2">
                Preview of one sheet — every sheet printed will look identical to this.
              </small>
            </div>

            {isPrinting && (
              <div className="mb-3">
                <Progress
                  value={printProgress.total ? (printProgress.current / printProgress.total) * 100 : 0}
                  color="primary"
                  striped
                  animated
                />
                <small className="text-muted">
                  Printing sheet {printProgress.current} of {printProgress.total}…
                </small>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onToggle} disabled={isPrinting}>
          Close
        </Button>
        <Button
          color="primary"
          onClick={handleSecurePrint}
          disabled={isPrinting || isLoadingImage || !!imageError || !previewImage || !selectedPrinter}
        >
          {isPrinting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Printing…
            </>
          ) : (
            <>
              <FaPrint className="me-2" />
              Print {numberOfPages} Sheet{numberOfPages !== 1 ? "s" : ""}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default BackBadgePrintModal
